import api from '../api'
import { API_URL } from '../../config'
import { getReconnectDelayMs } from './backoff'

// Phase 2 cutover (growchat-doc/realtime-inbox/
// spec-websocket-cutover-remove-polling.md) — synthetic event type dispatched
// through the same onEvent()/dispatch()/useWsEvent() pipeline as real server
// events (e.g. `notification.new`, `conversation.message`). Not a real
// server message; consumers (InboxPage.jsx, AppShell.jsx, DesktopShell.jsx)
// subscribe to it via useWsEvent(WS_RESYNC_EVENT_TYPE, ...) to trigger a
// one-shot REST refetch each time the WS session connects, replacing
// whatever REST polling used to reconcile.
export const WS_RESYNC_EVENT_TYPE = '__ws_resync__'

function wsUrlFromApiUrl(apiUrl) {
  try {
    const url = new URL(apiUrl)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    url.pathname = `${url.pathname.replace(/\/$/, '')}/ws/manager/session`
    url.search = ''
    return url
  } catch {
    return null
  }
}

/**
 * Session-level WS client (Track B FE1.1/FE1.6/FE1.7/FE1.8).
 * growchat-doc/realtime-inbox/spec-websocket-conversation-migration.md
 *
 * One instance per app session, created/owned by WsSessionContext's
 * Provider — not per-page. Confirmed against the actual backend code (not
 * assumed from the spec doc, which has some drift from what shipped):
 *
 *   - POST {API_URL}/manager/{tenantId}/ws-ticket (existing api.js auth
 *     headers apply) -> {"ticket": "...", "expires_in": 60}
 *     (app/routers/manager_ws.py: issue_manager_ws_ticket)
 *   - WS {API_URL}/ws/manager/session?ticket=... — server auto-subscribes
 *     the `notifications` topic for the ticket's tenant on connect, no
 *     client message needed for that part. Close code 4401 (ticket
 *     missing/expired/already redeemed) or 4404 (backend
 *     WS_REALTIME_ENABLED flag off) both mean "unavailable right now" —
 *     handled identically here (fall back, keep retrying on backoff).
 *   - Client -> server: {"type":"subscribe","topic":"conversation",
 *     "contact_id":...,"channel":...} to watch a thread,
 *     {"type":"unsubscribe","topic":"conversation"} to stop (no
 *     contact_id/channel on unsubscribe — the backend tracks membership
 *     per-connection server-side, confirmed in
 *     app/routers/manager_ws.py's _handle_client_message()), and
 *     {"type":"pong"} in reply to the server's {"type":"ping"} (sent every
 *     30s by app/routers/manager_ws.py's _heartbeat_loop()). Track B2
 *     (pagination-and-ws-tenant-wide, growchat-doc/realtime-inbox/
 *     spec-inbox-conversation-list-pagination.md) adds a second, separate
 *     topic — {"type":"subscribe","topic":"conversation_list"} /
 *     {"type":"unsubscribe","topic":"conversation_list"} — tenant-wide (no
 *     contact_id/channel param), fanning out a `conversation_list.upsert`
 *     event for every message written anywhere in the tenant so the sidebar
 *     can keep itself in sync even for threads never opened/subscribed.
 *   - Server -> client: whatever arrives is forwarded verbatim to
 *     `onEvent` (ping is intercepted and answered here, never forwarded).
 *     `onEvent` also receives one synthetic `WS_RESYNC_EVENT_TYPE` event per
 *     successful `onopen` (first connect and every reconnect) — see that
 *     const's doc comment above; Phase 2 cutover resync-on-reconnect.
 *
 * @param {object} opts
 * @param {string} opts.tenantId
 * @param {(event: object) => void} opts.onEvent
 * @param {(status: 'connected'|'disconnected') => void} [opts.onStatusChange]
 */
export function createRealtimeWsClient({ tenantId, onEvent, onStatusChange }) {
  let socket = null
  let disposed = true
  let connecting = false
  let reconnectAttempt = 0
  let reconnectTimer = null
  // FE1.8 — the thread the app currently wants watched. Persists across
  // reconnects so a fresh connection's onopen can re-subscribe it
  // immediately, without the caller having to notice the reconnect.
  let pendingSubscription = null // {contactId, channel} | null
  // Track B2 (pagination-and-ws-tenant-wide) — mirrors pendingSubscription's
  // reconnect-persistence, but for the tenant-wide `conversation_list`
  // topic (growchat-doc/realtime-inbox/
  // spec-inbox-conversation-list-pagination.md). No contact_id/channel to
  // remember here — it's a single on/off flag, not a {contactId, channel}
  // pair, since the topic itself has no parameters.
  let pendingConversationListSubscription = false

  function setStatus(status) {
    onStatusChange?.(status)
  }

  function clearReconnectTimer() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function scheduleReconnect() {
    if (disposed) return
    clearReconnectTimer()
    reconnectAttempt += 1
    const delay = getReconnectDelayMs(reconnectAttempt)
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void connect()
    }, delay)
  }

  function send(payload) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload))
      return true
    }
    return false
  }

  function sendSubscribe(contactId, channel) {
    send({ type: 'subscribe', topic: 'conversation', contact_id: contactId, channel })
  }

  function sendSubscribeConversationList() {
    send({ type: 'subscribe', topic: 'conversation_list' })
  }

  async function connect() {
    if (disposed || connecting || socket) return
    connecting = true
    try {
      const { data } = await api.post(`/manager/${encodeURIComponent(tenantId)}/ws-ticket`)
      const ticket = data?.ticket
      if (!ticket) throw new Error('ws-ticket response missing ticket')
      if (disposed) return

      const wsUrl = wsUrlFromApiUrl(API_URL)
      if (!wsUrl) throw new Error('could not derive WS URL from API_URL')
      wsUrl.searchParams.set('ticket', ticket)

      const ws = new WebSocket(wsUrl.toString())
      socket = ws

      ws.onopen = () => {
        connecting = false
        reconnectAttempt = 0
        setStatus('connected')
        if (pendingSubscription) {
          sendSubscribe(pendingSubscription.contactId, pendingSubscription.channel)
        }
        // Track B2 — re-subscribe the tenant-wide conversation_list topic on
        // every (re)connect too, same reconnect-persistence rationale as
        // pendingSubscription above.
        if (pendingConversationListSubscription) {
          sendSubscribeConversationList()
        }
        // Phase 2 cutover — fire the one-shot resync signal every time the
        // socket opens (first connect included, not just reconnects — see
        // WS_RESYNC_EVENT_TYPE doc above). Consumers own their own refetch
        // logic; this client only announces "the session just (re)opened".
        onEvent?.({ type: WS_RESYNC_EVENT_TYPE })
      }

      ws.onmessage = (evt) => {
        let data
        try {
          data = JSON.parse(evt.data)
        } catch {
          return
        }
        if (!data || typeof data !== 'object') return
        if (data.type === 'ping') {
          send({ type: 'pong' })
          return
        }
        onEvent?.(data)
      }

      ws.onclose = () => {
        connecting = false
        if (socket === ws) socket = null
        setStatus('disconnected')
        scheduleReconnect()
      }

      // onerror is always followed by onclose for the browser WebSocket API
      // — cleanup + reconnect scheduling lives there. Nothing to do here.
      ws.onerror = () => {}
    } catch {
      connecting = false
      socket = null
      setStatus('disconnected')
      scheduleReconnect()
    }
  }

  function subscribeConversation(contactId, channel) {
    if (!contactId || !channel) return
    const isSameSubscription =
      pendingSubscription?.contactId === contactId && pendingSubscription?.channel === channel
    if (isSameSubscription) return
    // FE1.5 — unsubscribe the previous thread (if any) before subscribing
    // to the new one.
    if (pendingSubscription) {
      send({ type: 'unsubscribe', topic: 'conversation' })
    }
    pendingSubscription = { contactId, channel }
    sendSubscribe(contactId, channel)
  }

  function unsubscribeConversation() {
    if (!pendingSubscription) return
    pendingSubscription = null
    send({ type: 'unsubscribe', topic: 'conversation' })
  }

  // Track B2 (pagination-and-ws-tenant-wide) — the tenant-wide
  // conversation_list topic. Same subscribe/unsubscribe message shape as
  // subscribeConversation/unsubscribeConversation above, minus contact_id/
  // channel (this topic has no parameters — it's one subscription per
  // connection, fanned out to every conversation of the tenant). No "switch
  // thread" semantics either since there's nothing to switch between.
  function subscribeConversationList() {
    if (pendingConversationListSubscription) return
    pendingConversationListSubscription = true
    sendSubscribeConversationList()
  }

  function unsubscribeConversationList() {
    if (!pendingConversationListSubscription) return
    pendingConversationListSubscription = false
    send({ type: 'unsubscribe', topic: 'conversation_list' })
  }

  function start() {
    if (!disposed) return
    disposed = false
    reconnectAttempt = 0
    void connect()
  }

  function stop() {
    disposed = true
    connecting = false
    clearReconnectTimer()
    pendingSubscription = null
    pendingConversationListSubscription = false
    if (socket) {
      const ws = socket
      socket = null
      ws.onopen = null
      ws.onmessage = null
      ws.onclose = null
      ws.onerror = null
      try {
        ws.close()
      } catch {
        // already closed/closing — nothing to do
      }
    }
  }

  return {
    connect: start,
    disconnect: stop,
    subscribeConversation,
    unsubscribeConversation,
    subscribeConversationList,
    unsubscribeConversationList,
  }
}
